import React, { useEffect, useRef, useCallback } from 'react';

/**
 * FocusTrap
 *
 * Traps keyboard focus inside the wrapped subtree for the lifetime of
 * the component. Intended for custom modals, side-drawers, and other
 * dialogs that need to satisfy:
 *   - WCAG 2.1.2 No Keyboard Trap (focus returns on Escape)
 *   - WCAG 2.4.3 Focus Order
 *   - WCAG 2.4.11 Focus Not Obscured
 *
 * Radix primitives already trap focus, so prefer their Dialog/Popover
 * over this. Use FocusTrap only for bespoke overlays.
 *
 * Props:
 *   active        — when false, the trap is bypassed (default true)
 *   onEscape      — optional callback when Escape is pressed
 *   returnFocus   — restore focus to the previously focused element on
 *                   unmount (default true)
 *   initialFocus  — ref to the element that should receive focus on
 *                   mount. Falls back to the first tabbable descendant.
 *
 * Usage:
 *   <FocusTrap active={open} onEscape={close}>
 *     <div role="dialog" aria-modal="true">…</div>
 *   </FocusTrap>
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

function getTabbable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
  );
}

export function FocusTrap({
  active = true,
  onEscape,
  returnFocus = true,
  initialFocus,
  children,
}) {
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (!active) return;

      if (event.key === 'Escape' && onEscape) {
        event.stopPropagation();
        onEscape(event);
        return;
      }

      if (event.key !== 'Tab') return;

      const tabbables = getTabbable(containerRef.current);
      if (tabbables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const activeEl = document.activeElement;

      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [active, onEscape]
  );

  useEffect(() => {
    if (!active) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const target =
      (initialFocus && initialFocus.current) ||
      getTabbable(containerRef.current)[0] ||
      containerRef.current;
    if (target && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (returnFocus && previouslyFocusedRef.current) {
        const prev = previouslyFocusedRef.current;
        if (prev && typeof prev.focus === 'function') {
          prev.focus({ preventScroll: true });
        }
      }
    };
  }, [active, handleKeyDown, initialFocus, returnFocus]);

  return (
    <div ref={containerRef} tabIndex={-1} data-focus-trap={active ? 'on' : 'off'}>
      {children}
    </div>
  );
}

export default FocusTrap;
